import React from 'react';
import Konva from 'konva';
import { Stage, Layer, Circle, Text, Image as KonvaImage, Group, Rect } from 'react-konva';

const STATUS_COLORS = {
    available: null,
    accessible: null,
    owned: null,
    selected: '#ffc107',
    reserved: '#dc3545',
    unavailable: '#adb5bd',
    unmapped: '#dee2e6',
    inventoryDisabled: '#ccc',
};

const SEAT_VISUAL_SCALE = 1.5;
const DEFAULT_SEAT_RADIUS = 6;
const SEAT_HIT_PADDING = 4;
const MIN_USER_SCALE = 1;
const MAX_USER_SCALE = 4;
const VIEWPORT_HEIGHT_RATIO = 0.85;
const MOBILE_VIEWPORT_HEIGHT_RATIO = 0.5;
const VIEWPORT_MIN_HEIGHT = 560;
const VIEWPORT_MAX_HEIGHT = 920;
const MOBILE_VIEWPORT_MIN_HEIGHT = 280;
const MOBILE_TAP_PADDING = 12;
const MOBILE_SCREEN_TAP_PADDING = 24;
const MOBILE_DRAG_DISTANCE = 12;

function getDeviceProfile() {
    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    const narrow = window.matchMedia('(max-width: 768px)').matches;
    const isMobile = coarsePointer || narrow;

    return {
        isMobile,
        pixelRatio: isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 2),
        viewportMinHeight: isMobile ? MOBILE_VIEWPORT_MIN_HEIGHT : VIEWPORT_MIN_HEIGHT,
        viewportHeightRatio: isMobile ? MOBILE_VIEWPORT_HEIGHT_RATIO : VIEWPORT_HEIGHT_RATIO,
        seatVisualScale: isMobile ? 1.25 : SEAT_VISUAL_SCALE,
    };
}

function getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt((dx * dx) + (dy * dy));
}

function getTouchCenter(touches, container) {
    const rect = container.getBoundingClientRect();
    return {
        x: ((touches[0].clientX + touches[1].clientX) / 2) - rect.left,
        y: ((touches[0].clientY + touches[1].clientY) / 2) - rect.top,
    };
}

const InteractiveSeat = React.memo(function InteractiveSeat({
    seat,
    selected,
    fill,
    selectable,
    radius,
    showLabels,
    enableHover,
    useLayerSelection,
    onSeatClick,
    onSeatMouseEnter,
    onSeatMouseLeave,
}) {
    const hitPadding = enableHover ? SEAT_HIT_PADDING : MOBILE_TAP_PADDING;
    const useLayerTap = useLayerSelection === true;

    return (
        <Group
            x={seat.x}
            y={seat.y}
            listening={selectable && !useLayerTap}
            onMouseEnter={enableHover ? () => onSeatMouseEnter(seat) : undefined}
            onMouseLeave={enableHover ? onSeatMouseLeave : undefined}
            onClick={!useLayerTap && selectable ? () => onSeatClick(seat) : undefined}
            onTap={!useLayerTap && selectable ? () => onSeatClick(seat) : undefined}
        >
            {selectable && !useLayerTap && (
                <Circle
                    radius={radius + hitPadding}
                    fill="transparent"
                    listening={true}
                    perfectDrawEnabled={false}
                />
            )}
            <Circle
                radius={radius}
                fill={fill}
                stroke={selected ? '#212529' : '#ffffff'}
                strokeWidth={selected ? 2.5 : 1.5}
                opacity={selected ? 0.95 : (selectable ? 0.78 : 0.35)}
                listening={false}
                perfectDrawEnabled={false}
            />
            {showLabels && radius >= 7 && (
                <Text
                    text={String(seat.seatLabel)}
                    fontSize={Math.max(8, Math.round(radius * 0.85))}
                    fill={selected ? '#212529' : '#ffffff'}
                    width={radius * 2}
                    height={radius * 2}
                    offsetX={radius}
                    offsetY={radius - 4}
                    align="center"
                    listening={false}
                    perfectDrawEnabled={false}
                />
            )}
        </Group>
    );
}, (prev, next) => (
    prev.seat.id === next.seat.id
    && prev.selected === next.selected
    && prev.fill === next.fill
    && prev.selectable === next.selectable
    && prev.radius === next.radius
    && prev.enableHover === next.enableHover
    && prev.showLabels === next.showLabels
    && prev.useLayerSelection === next.useLayerSelection
));

export class InteractiveSeating extends React.Component {
    constructor(props) {
        super(props);
        this.stageRef = React.createRef();
        this.containerRef = React.createRef();
        this.staticLayerRef = React.createRef();
        this.deviceProfile = getDeviceProfile();
        Konva.pixelRatio = this.deviceProfile.pixelRatio;
        if (this.deviceProfile.isMobile) {
            Konva.hitOnDragEnabled = true;
        }

        this._view = {
            userScale: 1,
            stageX: 0,
            stageY: 0,
        };
        this._layoutInitialized = false;
        this._isPinching = false;

        this.state = {
            selectedIds: {},
            selectedCategoryId: null,
            showCategoryModal: false,
            isPinching: false,
            sections: props.sections ? JSON.parse(JSON.stringify(props.sections)) : [],
            userScale: 1,
            stageX: 0,
            stageY: 0,
            bgImage: null,
            containerWidth: 800,
            viewportHeight: this.deviceProfile.viewportMinHeight,
            hoveredSeat: null,
            tooltipPosition: { x: 0, y: 0 },
        };

        this.handleSeatClick = this.handleSeatClick.bind(this);
        this.handleWheel = this.handleWheel.bind(this);
        this.handleDragEnd = this.handleDragEnd.bind(this);
        this.handleStageDragStart = this.handleStageDragStart.bind(this);
        this.dragBoundFunc = this.dragBoundFunc.bind(this);
        this.clearSeatTooltip = this.clearSeatTooltip.bind(this);
        this.getSectionCategoryId = this.getSectionCategoryId.bind(this);
        this.getSeatColor = this.getSeatColor.bind(this);
        this.notifySelectionChange = this.notifySelectionChange.bind(this);
        this.updateContainerWidth = this.updateContainerWidth.bind(this);
        this.handleSeatMouseEnter = this.handleSeatMouseEnter.bind(this);
        this.handleSeatMouseLeave = this.handleSeatMouseLeave.bind(this);
        this.applyInitialSelection = this.applyInitialSelection.bind(this);
        this.signalReady = this.signalReady.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handlePointerSeatSelect = this.handlePointerSeatSelect.bind(this);
        this.handleStageTap = this.handleStageTap.bind(this);
        this.resetView = this.resetView.bind(this);
        this.zoomIn = this.zoomIn.bind(this);
        this.zoomOut = this.zoomOut.bind(this);
        this.clearSelection = this.clearSelection.bind(this);
        this.deselectSeat = this.deselectSeat.bind(this);
        this.closeCategoryModal = this.closeCategoryModal.bind(this);
    }

    getPublicApi() {
        return {
            clearSelection: this.clearSelection,
            deselectSeat: this.deselectSeat,
            zoomIn: this.zoomIn,
            zoomOut: this.zoomOut,
            resetView: this.resetView,
        };
    }

    signalReady() {
        if (this._readySignaled) {
            return;
        }
        this._readySignaled = true;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (typeof this.props.onReady === 'function') {
                    this.props.onReady(this.getPublicApi());
                }
            });
        });
    }

    applyInitialSelection() {
        const initialSeats = this.props.initialSelectedSeats || [];
        if (!initialSeats.length) {
            return;
        }

        const selectedIds = {};
        let selectedCategoryId = null;

        initialSeats.forEach((seat) => {
            selectedIds[seat.id] = {
                id: seat.id,
                section: seat.section,
                sectionName: this.getSectionName(seat.section),
                rowLabel: seat.rowLabel,
                seatLabel: seat.seatLabel,
                label: `${seat.rowLabel} / ${seat.seatLabel}`,
            };
            selectedCategoryId = this.getSeatCategoryId(seat);
        });

        this.setState({
            selectedIds,
            selectedCategoryId,
        }, () => this.notifySelectionChange(selectedIds, selectedCategoryId));
    }

    componentDidMount() {
        this.updateContainerWidth();
        window.addEventListener('resize', this.updateContainerWidth);
        this.bindMobileTouchHandlers();

        if (this.props.backgroundImage) {
            const img = new window.Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => this.setState({ bgImage: img }, () => {
                this.applyStagePixelRatio();
                this.signalReady();
                requestAnimationFrame(() => this.cacheStaticLayer());
            });
            img.onerror = () => this.signalReady();
            img.src = this.props.backgroundImage;
        } else {
            this.applyStagePixelRatio();
            this.signalReady();
            requestAnimationFrame(() => this.cacheStaticLayer());
        }

        this.applyInitialSelection();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.sections !== this.props.sections && this.props.sections) {
            this._staticLayerCached = false;
            this.setState({ sections: JSON.parse(JSON.stringify(this.props.sections)) });
        }

        if (prevProps.initialSelectedSeats !== this.props.initialSelectedSeats && (this.props.initialSelectedSeats || []).length > 0) {
            this.applyInitialSelection();
        }

        if (prevState.bgImage !== this.state.bgImage && this.state.bgImage) {
            requestAnimationFrame(() => this.cacheStaticLayer());
        }

        if (
            prevState.containerWidth !== this.state.containerWidth
            || prevState.viewportHeight !== this.state.viewportHeight
        ) {
            this.applyStagePixelRatio();
            if (this._staticLayerCached && this.staticLayerRef.current) {
                this.staticLayerRef.current.clearCache();
                this._staticLayerCached = false;
                requestAnimationFrame(() => this.cacheStaticLayer());
            }
        }

        if (!this._touchHandlersBound && this.containerRef.current && this.deviceProfile.isMobile) {
            this.bindMobileTouchHandlers();
        }

    }

    bindMobileTouchHandlers() {
        const container = this.containerRef.current;
        if (!container || this._touchHandlersBound) {
            return;
        }

        container.addEventListener('touchstart', this.handleTouchStart, { passive: false });
        container.addEventListener('touchmove', this.handleTouchMove, { passive: false });
        container.addEventListener('touchend', this.handleTouchEnd);
        container.addEventListener('touchcancel', this.handleTouchEnd);
        this._touchHandlersBound = true;
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateContainerWidth);

        const container = this.containerRef.current;
        if (container && this._touchHandlersBound) {
            container.removeEventListener('touchstart', this.handleTouchStart);
            container.removeEventListener('touchmove', this.handleTouchMove);
            container.removeEventListener('touchend', this.handleTouchEnd);
            container.removeEventListener('touchcancel', this.handleTouchEnd);
        }

        if (this._resizeRaf) {
            cancelAnimationFrame(this._resizeRaf);
        }
        if (this._pinchRaf) {
            cancelAnimationFrame(this._pinchRaf);
        }

    }

    applyStagePixelRatio() {
        const stage = this.stageRef.current;
        if (!stage) {
            return;
        }

        const konvaStage = stage.getStage ? stage.getStage() : stage;
        if (konvaStage && typeof konvaStage.pixelRatio === 'function') {
            konvaStage.pixelRatio(this.deviceProfile.pixelRatio);
        }
    }

    cacheStaticLayer() {
        if (this._staticLayerCached || this.deviceProfile.isMobile) {
            return;
        }

        const layer = this.staticLayerRef.current;
        if (!layer || typeof layer.cache !== 'function') {
            return;
        }

        layer.cache();
        if (typeof layer.batchDraw === 'function') {
            layer.batchDraw();
        }
        this._staticLayerCached = true;
    }

    updateContainerWidth() {
        if (this._resizeRaf) {
            cancelAnimationFrame(this._resizeRaf);
        }

        this._resizeRaf = requestAnimationFrame(() => {
            this._resizeRaf = null;
            this.updateContainerWidthNow();
        });
    }

    getCenteredPosition(containerWidth, viewportHeight, userScale) {
        const canvas = this.props.canvas || { width: 821, height: 1102 };
        const fitScale = this.getFitScaleForSize(containerWidth, viewportHeight, canvas);
        const scaledWidth = canvas.width * fitScale * userScale;
        const scaledHeight = canvas.height * fitScale * userScale;

        return {
            stageX: (containerWidth - scaledWidth) / 2,
            stageY: (viewportHeight - scaledHeight) / 2,
        };
    }

    clampStagePosition(stageX, stageY, userScale, containerWidth = null, viewportHeight = null) {
        const canvas = this.props.canvas || { width: 821, height: 1102 };
        const cw = containerWidth !== null ? containerWidth : this.state.containerWidth;
        const ch = viewportHeight !== null ? viewportHeight : this.state.viewportHeight;
        const fitScale = this.getFitScaleForSize(cw, ch, canvas);
        const totalScale = fitScale * userScale;
        const scaledWidth = canvas.width * totalScale;
        const scaledHeight = canvas.height * totalScale;

        let minX;
        let maxX;
        let minY;
        let maxY;

        if (scaledWidth <= cw) {
            const centeredX = (cw - scaledWidth) / 2;
            minX = centeredX;
            maxX = centeredX;
        } else {
            minX = cw - scaledWidth;
            maxX = 0;
        }

        if (scaledHeight <= ch) {
            const centeredY = (ch - scaledHeight) / 2;
            minY = centeredY;
            maxY = centeredY;
        } else {
            minY = ch - scaledHeight;
            maxY = 0;
        }

        return {
            x: Math.min(maxX, Math.max(minX, stageX)),
            y: Math.min(maxY, Math.max(minY, stageY)),
        };
    }

    applyViewToStage() {
        const stageNode = this.stageRef.current;
        if (!stageNode) {
            return;
        }

        const stage = stageNode.getStage ? stageNode.getStage() : stageNode;
        const totalScale = this.getFitScale() * this._view.userScale;
        stage.scale({ x: totalScale, y: totalScale });
        stage.position({ x: this._view.stageX, y: this._view.stageY });
        if (typeof stage.batchDraw === 'function') {
            stage.batchDraw();
        }
    }

    syncViewToState(extra = {}) {
        this.setState({
            userScale: this._view.userScale,
            stageX: this._view.stageX,
            stageY: this._view.stageY,
            ...extra,
        });
    }

    setStageDraggable(enabled) {
        const stageNode = this.stageRef.current;
        if (!stageNode) {
            return;
        }

        const stage = stageNode.getStage ? stageNode.getStage() : stageNode;
        if (typeof stage.draggable === 'function') {
            stage.draggable(enabled);
        }
        if (!enabled && typeof stage.stopDrag === 'function') {
            stage.stopDrag();
        }
    }

    updateContainerWidthNow() {
        if (!this.containerRef.current) {
            return;
        }

        const containerWidth = this.containerRef.current.offsetWidth || 800;
        const parentHeight = this.containerRef.current.parentElement
            ? this.containerRef.current.parentElement.clientHeight
            : 0;
        const fillParent = this.props.fillParentHeight === true && parentHeight > 120;
        const viewportHeight = fillParent
            ? parentHeight
            : containerWidth;

        if (!this._layoutInitialized) {
            const centered = this.getCenteredPosition(containerWidth, viewportHeight, this._view.userScale);
            this._view.stageX = centered.stageX;
            this._view.stageY = centered.stageY;
            this._layoutInitialized = true;
            this.setState({
                containerWidth,
                viewportHeight,
                userScale: this._view.userScale,
                stageX: this._view.stageX,
                stageY: this._view.stageY,
            });
            return;
        }

        const clamped = this.clampStagePosition(
            this._view.stageX,
            this._view.stageY,
            this._view.userScale,
            containerWidth,
            viewportHeight
        );
        this._view.stageX = clamped.x;
        this._view.stageY = clamped.y;

        this.setState({
            containerWidth,
            viewportHeight,
            stageX: this._view.stageX,
            stageY: this._view.stageY,
        }, () => this.applyViewToStage());
    }

    getFitScaleForSize(containerWidth, viewportHeight, canvas) {
        const widthScale = containerWidth / canvas.width;
        const heightScale = viewportHeight / canvas.height;
        return Math.min(widthScale, heightScale);
    }

    getFitScale() {
        const canvas = this.props.canvas || { width: 821, height: 1102 };
        return this.getFitScaleForSize(this.state.containerWidth, this.state.viewportHeight, canvas);
    }

    getTotalScale() {
        return this.getFitScale() * this._view.userScale;
    }

    applyZoomAtPoint(pointer, newUserScale, { syncState = true } = {}) {
        const clampedUserScale = Math.max(MIN_USER_SCALE, Math.min(MAX_USER_SCALE, newUserScale));
        const fitScale = this.getFitScale();
        const oldTotalScale = fitScale * this._view.userScale;
        const mousePointTo = {
            x: (pointer.x - this._view.stageX) / oldTotalScale,
            y: (pointer.y - this._view.stageY) / oldTotalScale,
        };
        const newTotalScale = fitScale * clampedUserScale;
        const nextX = pointer.x - (mousePointTo.x * newTotalScale);
        const nextY = pointer.y - (mousePointTo.y * newTotalScale);
        const clamped = this.clampStagePosition(nextX, nextY, clampedUserScale);

        this._view.userScale = clampedUserScale;
        this._view.stageX = clamped.x;
        this._view.stageY = clamped.y;
        this.applyViewToStage();

        if (syncState) {
            this.syncViewToState();
        }
    }

    flushPendingPinchZoom() {
        if (this._pinchRaf) {
            cancelAnimationFrame(this._pinchRaf);
            this._pinchRaf = null;
        }

        if (this._pinchPendingZoom) {
            const { pointer, newUserScale } = this._pinchPendingZoom;
            this._pinchPendingZoom = null;
            this.applyZoomAtPoint(pointer, newUserScale, { syncState: false });
        }
    }

    endPinchGesture() {
        if (!this._isPinching && !this.pinchStartDistance) {
            return;
        }

        this.flushPendingPinchZoom();
        this._isPinching = false;
        this.pinchStartDistance = null;
        this.pinchStartScale = null;
        this.setStageDraggable(true);
        this.syncViewToState({ isPinching: false });
    }

    resetView() {
        this.clearSeatTooltip();
        this._view.userScale = 1;
        const centered = this.getCenteredPosition(
            this.state.containerWidth,
            this.state.viewportHeight,
            1
        );
        this._view.stageX = centered.stageX;
        this._view.stageY = centered.stageY;
        this.applyViewToStage();
        this.syncViewToState();
    }

    getViewportCenterPointer() {
        return {
            x: (this.state.containerWidth || 0) / 2,
            y: (this.state.viewportHeight || 0) / 2,
        };
    }

    zoomByFactor(factor) {
        this.clearSeatTooltip();
        const newUserScale = this._view.userScale * factor;
        this.applyZoomAtPoint(this.getViewportCenterPointer(), newUserScale, { syncState: true });
    }

    zoomIn() {
        this.zoomByFactor(1.08);
    }

    zoomOut() {
        this.zoomByFactor(1 / 1.08);
    }

    clearSelection() {
        this.setState({
            selectedIds: {},
            selectedCategoryId: null,
        }, () => this.notifySelectionChange({}, null));
    }

    deselectSeat(seatId) {
        if (!seatId || !this.state.selectedIds[seatId]) {
            return;
        }

        const selectedIds = { ...this.state.selectedIds };
        delete selectedIds[seatId];
        const selectedCategoryId = Object.keys(selectedIds).length === 0
            ? null
            : this.state.selectedCategoryId;

        this.setState({
            selectedIds,
            selectedCategoryId,
        }, () => this.notifySelectionChange(selectedIds, selectedCategoryId));
    }

    closeCategoryModal() {
        this.setState({ showCategoryModal: false });
    }

    handleTouchStart(e) {
        if (e.touches.length === 2) {
            e.preventDefault();
            this.clearSeatTooltip();
            this.flushPendingPinchZoom();
            this._isPinching = true;
            this.pinchStartDistance = getTouchDistance(e.touches);
            this.pinchStartScale = this._view.userScale;
            this._mobileTapStart = null;
            this._mobileTapMoved = false;
            this.setStageDraggable(false);
            this.syncViewToState({ isPinching: true });
            return;
        }

        if (e.touches.length === 1 && this.deviceProfile.isMobile) {
            this._mobileTapStart = {
                clientX: e.touches[0].clientX,
                clientY: e.touches[0].clientY,
                time: Date.now(),
            };
            this._mobileTapMoved = false;
        }
    }

    handleTouchMove(e) {
        if (e.touches.length === 2 && this.pinchStartDistance) {
            e.preventDefault();
            const container = this.containerRef.current;
            if (!container) {
                return;
            }

            const distance = getTouchDistance(e.touches);
            const scaleFactor = distance / this.pinchStartDistance;
            const pointer = getTouchCenter(e.touches, container);
            const newUserScale = this.pinchStartScale * scaleFactor;

            this._pinchPendingZoom = { pointer, newUserScale };

            if (this._pinchRaf) {
                return;
            }

            this._pinchRaf = requestAnimationFrame(() => {
                this._pinchRaf = null;
                if (this._pinchPendingZoom) {
                    const { pointer: pendingPointer, newUserScale: pendingScale } = this._pinchPendingZoom;
                    this._pinchPendingZoom = null;
                    this.applyZoomAtPoint(pendingPointer, pendingScale, { syncState: false });
                }
            });
            return;
        }

        if (e.touches.length === 1 && this._mobileTapStart && this.deviceProfile.isMobile) {
            const dx = e.touches[0].clientX - this._mobileTapStart.clientX;
            const dy = e.touches[0].clientY - this._mobileTapStart.clientY;
            if (Math.hypot(dx, dy) > MOBILE_DRAG_DISTANCE) {
                this._mobileTapMoved = true;
            }
        }
    }

    handleTouchEnd(e) {
        if (e.touches.length < 2) {
            this.endPinchGesture();
        }

        if (
            this.deviceProfile.isMobile
            && this._mobileTapStart
            && !this._mobileTapMoved
            && !this._isPinching
            && e.changedTouches.length > 0
            && e.touches.length === 0
        ) {
            const touch = e.changedTouches[0];
            const pos = this.getStagePosFromClient(touch.clientX, touch.clientY);
            if (pos) {
                this.selectNearestSeatAtStagePos(pos);
            }
        }

        if (e.touches.length === 0) {
            this._mobileTapStart = null;
            this._mobileTapMoved = false;
        }
    }

    getStagePosFromClient(clientX, clientY) {
        const stageNode = this.stageRef.current;
        if (!stageNode) {
            return null;
        }

        const stage = stageNode.getStage ? stageNode.getStage() : stageNode;
        const container = stage.container ? stage.container() : null;
        if (!container) {
            return null;
        }

        const rect = container.getBoundingClientRect();
        const pointer = {
            x: clientX - rect.left,
            y: clientY - rect.top,
        };
        const transform = stage.getAbsoluteTransform().copy().invert();
        return transform.point(pointer);
    }

    getMobileTapRadius() {
        const seatScale = this.props.seatScale || this.deviceProfile.seatVisualScale;
        const visualRadius = DEFAULT_SEAT_RADIUS * seatScale;
        const stagePadding = MOBILE_SCREEN_TAP_PADDING / this.getTotalScale();
        return visualRadius + stagePadding;
    }

    selectNearestSeatAtStagePos(pos) {
        if (!pos) {
            return;
        }

        const tapRadius = this.getMobileTapRadius();
        const seats = this.props.seats || [];
        let nearestSeat = null;
        let nearestDistance = tapRadius;

        seats.forEach((seat) => {
            if (!this.isSeatSelectable(seat)) {
                return;
            }

            const distance = Math.hypot(seat.x - pos.x, seat.y - pos.y);
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestSeat = seat;
            }
        });

        if (nearestSeat) {
            this.handleSeatClick(nearestSeat);
        }
    }

    handlePointerSeatSelect(e) {
        if (!this.deviceProfile.isMobile || !e || !e.target) {
            return;
        }

        const stage = e.target.getStage();
        if (!stage) {
            return;
        }

        let pos = stage.getRelativePointerPosition();
        if (!pos) {
            const pointer = stage.getPointerPosition();
            if (!pointer) {
                return;
            }
            const transform = stage.getAbsoluteTransform().copy().invert();
            pos = transform.point(pointer);
        }

        this.selectNearestSeatAtStagePos(pos);
    }

    handleStageTap(e) {
        if (!this.deviceProfile.isMobile || this._isPinching || this.state.isPinching) {
            return;
        }

        this.handlePointerSeatSelect(e);
    }

    getAvailableCategoryIds() {
        return new Set(
            (this.props.categories || [])
                .map((category) => parseInt(category.id, 10))
                .filter((id) => !Number.isNaN(id))
        );
    }

    pickAvailableCategoryId(categoryIds) {
        const available = this.getAvailableCategoryIds();
        const normalized = (categoryIds || [])
            .map((id) => parseInt(id, 10))
            .filter((id) => !Number.isNaN(id));

        const matched = normalized.find((id) => available.has(id));
        if (matched !== undefined) {
            return matched;
        }

        return normalized.length > 0 ? normalized[0] : null;
    }

    getSeatCategoryId(seat) {
        if (seat && seat.categoryId !== undefined && seat.categoryId !== null) {
            const seatCategoryId = parseInt(seat.categoryId, 10);
            if (!Number.isNaN(seatCategoryId)) {
                const resolved = this.pickAvailableCategoryId([seatCategoryId]);
                if (resolved !== null) {
                    return resolved;
                }
            }
        }

        return this.getSectionCategoryId(seat ? seat.section : 0);
    }

    getSectionCategoryId(sectionIndex) {
        const key = parseInt(sectionIndex, 10);
        const categoriesBySection = this.props.sectionCategoriesMap || {};
        const categoryIds = categoriesBySection[key] || categoriesBySection[String(key)] || [];

        if (this.state.selectedCategoryId !== null) {
            const selectedId = parseInt(this.state.selectedCategoryId, 10);
            if (categoryIds.some((id) => parseInt(id, 10) === selectedId)) {
                return selectedId;
            }
        }

        if (categoryIds.length > 0) {
            const resolved = this.pickAvailableCategoryId(categoryIds);
            if (resolved !== null) {
                return resolved;
            }
        }

        const lookup = this.props.sectionToCategoryMap || {};
        if (lookup[key] !== undefined || lookup[String(key)] !== undefined) {
            const resolved = this.pickAvailableCategoryId([lookup[key] ?? lookup[String(key)]]);
            if (resolved !== null) {
                return resolved;
            }
        }

        const map = this.props.sectionCategoryMap || {};
        for (const [categoryId, sectionIds] of Object.entries(map)) {
            const sections = Array.isArray(sectionIds) ? sectionIds : [sectionIds];
            if (sections.some((id) => parseInt(id, 10) === key)) {
                const resolved = this.pickAvailableCategoryId([categoryId]);
                if (resolved !== null) {
                    return resolved;
                }
            }
        }

        const available = [...this.getAvailableCategoryIds()];
        return available.length === 1 ? available[0] : null;
    }

    getSectionName(sectionIndex) {
        const meta = this.props.sectionMeta || [];
        const key = parseInt(sectionIndex, 10);
        const entry = meta.find((m) => parseInt(m.index, 10) === key);
        return entry ? entry.name : '';
    }

    getSeatTooltipText(seat) {
        const sectionName = this.getSectionName(seat.section);
        const row = this.props.rowLabel || 'Row';
        const seatLabel = this.props.seatLabel || 'Seat';
        return `${sectionName} · ${row}: ${seat.rowLabel} · ${seatLabel}: ${seat.seatLabel}`;
    }

    clearSeatTooltip() {
        if (this.containerRef.current) {
            this.containerRef.current.style.cursor = '';
        }

        if (this.state.hoveredSeat) {
            this.setState({ hoveredSeat: null });
        }
    }

    updateTooltipPosition() {
        const stage = this.stageRef.current;
        const container = this.containerRef.current;
        if (!stage || !container) {
            return null;
        }

        const pointer = stage.getPointerPosition();
        if (!pointer) {
            return null;
        }

        const rect = container.getBoundingClientRect();
        return {
            x: rect.left + pointer.x,
            y: rect.top + pointer.y,
        };
    }

    getCategoryColorForId(categoryId) {
        const parsedCategoryId = parseInt(categoryId, 10);
        if (Number.isNaN(parsedCategoryId)) {
            return '#e91e63';
        }

        if (this.props.categoryColors && this.props.categoryColors[parsedCategoryId]) {
            return this.props.categoryColors[parsedCategoryId];
        }

        const categories = this.props.categories || [];
        const cat = categories.find((c) => parseInt(c.id, 10) === parsedCategoryId);
        return cat ? cat.color : '#e91e63';
    }

    getCategoryColor(seatOrSection) {
        const categoryId = seatOrSection && typeof seatOrSection === 'object'
            ? this.getSeatCategoryId(seatOrSection)
            : this.getSectionCategoryId(seatOrSection);

        return this.getCategoryColorForId(categoryId);
    }

    formatCategoryPrice(category) {
        if (!category) {
            return '';
        }

        if (category.free === true || category.free === 1 || category.free === '1') {
            return this.props.freeLabel || 'FREE';
        }

        const price = parseFloat(category.price);
        if (Number.isNaN(price)) {
            return category.price_description || '';
        }

        const priceFormat = this.props.priceFormat || '€%s';
        return priceFormat.replace('%s', price.toFixed(2));
    }

    renderCategoryLegendItems() {
        const categories = this.props.categories || [];

        return categories.map((category) => {
            const categoryId = parseInt(category.id, 10);
            const color = this.getCategoryColorForId(categoryId);
            const priceLabel = this.formatCategoryPrice(category);

            return (
                <div key={categoryId} className="d-flex flex-column align-items-center mx-2">
                    <span
                        className="d-inline-block rounded-circle"
                        style={{ width: 18, height: 18, backgroundColor: color }}
                    />
                    <small><em>{category.name}{priceLabel ? ` · ${priceLabel}` : ''}</em></small>
                </div>
            );
        });
    }

    getSeatColor(seat) {
        if (seat.inventoryDisabled === true) {
            return STATUS_COLORS.inventoryDisabled;
        }
        const status = seat.status || 'available';
        if (status === 'selected' || this.state.selectedIds[seat.id]) {
            return STATUS_COLORS.selected;
        }
        if (status === 'reserved') {
            return STATUS_COLORS.reserved;
        }
        if (status === 'owned') {
            return this.getCategoryColor(seat);
        }
        if (status === 'unavailable' || status === 'unmapped') {
            return STATUS_COLORS[status];
        }
        return this.getCategoryColor(seat);
    }

    isSeatSelectable(seat) {
        if (seat.inventoryDisabled === true) {
            return false;
        }
        const status = seat.status || 'available';
        if (status === 'reserved' || status === 'unavailable' || status === 'unmapped') {
            return false;
        }
        if (this.state.selectedIds[seat.id]) {
            return true;
        }
        return status === 'available' || status === 'accessible' || status === 'owned';
    }

    buildVenueMap() {
        const { sections, selectedIds } = this.state;
        const seats = this.props.seats || [];
        const selectedSeatIds = Object.keys(selectedIds);

        if (selectedSeatIds.length === 0) {
            return sections;
        }

        const rowPatches = new Map();

        seats.forEach((seat) => {
            if (!selectedIds[seat.id]) {
                return;
            }

            const patchKey = `${seat.section}:${seat.gridRow}`;
            if (!rowPatches.has(patchKey)) {
                rowPatches.set(patchKey, {
                    section: seat.section,
                    row: seat.gridRow,
                    cells: [],
                });
            }

            rowPatches.get(patchKey).cells.push(seat);
        });

        const nextSections = Array.isArray(sections) ? sections.slice() : { ...sections };

        rowPatches.forEach(({ section, row, cells }) => {
            if (!nextSections[section] || !nextSections[section].rows || !nextSections[section].rows[row]) {
                return;
            }

            const patchedRows = nextSections[section].rows.slice();
            const patchedRow = patchedRows[row].slice();

            cells.forEach((seat) => {
                const base = patchedRow[seat.gridCol];
                patchedRow[seat.gridCol] = (base === 'w' || seat.accessible) ? 'w0' : 0;
            });

            patchedRows[row] = patchedRow;
            nextSections[section] = {
                ...nextSections[section],
                rows: patchedRows,
            };
        });

        return nextSections;
    }

    buildSelectionSummaryLines(reservation) {
        const rowText = this.props.rowLabel || 'Row';
        const seatText = this.props.seatLabel || 'Seat';
        const seatsText = this.props.seatsLabel || 'Seats';
        const groups = new Map();

        reservation.forEach((seat) => {
            const sectionName = seat.sectionName || this.getSectionName(seat.section);
            const key = `${sectionName}||${seat.rowLabel}`;
            if (!groups.has(key)) {
                groups.set(key, {
                    sectionName,
                    rowLabel: seat.rowLabel,
                    seatLabels: [],
                });
            }
            groups.get(key).seatLabels.push(String(seat.seatLabel));
        });

        return Array.from(groups.values()).map((group) => {
            const seatList = group.seatLabels.join(', ');
            const seatWord = group.seatLabels.length > 1 ? seatsText : seatText;
            const rowPart = `${rowText} ${group.rowLabel} · ${seatWord}: ${seatList}`;
            return group.sectionName ? `${group.sectionName} — ${rowPart}` : rowPart;
        });
    }

    notifySelectionChange(selectedIds, selectedCategoryId) {
        const reservation = Object.values(selectedIds);
        if (typeof this.props.onSelectionChange === 'function') {
            this.props.onSelectionChange({
                categoryId: selectedCategoryId,
                quantity: reservation.length,
                venueMap: this.buildVenueMap(),
                reservation,
                summaryLines: this.buildSelectionSummaryLines(reservation),
            });
        }
    }

    getMaxSelectableSeats(categoryId) {
        const fallback = this.props.maxSelectableSeats || 10;
        const limitsMap = this.props.categoryLimits || {};
        const fromMap = limitsMap[categoryId] || limitsMap[String(categoryId)];
        const fromCategories = (this.props.categories || []).find(
            (item) => String(item.id) === String(categoryId)
        );
        const category = fromMap || fromCategories;

        if (!category) {
            return fallback;
        }

        const limitPerUser = parseInt(category.limit_per_user, 10);
        if (!(limitPerUser > 0)) {
            return fallback;
        }

        const limitLeft = parseInt(category.limit_per_user_left, 10);
        if (!Number.isNaN(limitLeft)) {
            return Math.max(0, limitLeft);
        }

        return limitPerUser;
    }

    handleSeatClick(seat) {
        if (!this.isSeatSelectable(seat)) {
            return;
        }

        const now = Date.now();
        if (this._lastSeatSelectTs && (now - this._lastSeatSelectTs) < 250) {
            return;
        }
        this._lastSeatSelectTs = now;

        const seatCategoryId = this.getSeatCategoryId(seat);
        const maxSeats = this.getMaxSelectableSeats(seatCategoryId);
        let { selectedIds, selectedCategoryId } = this.state;

        if (selectedIds[seat.id]) {
            const next = { ...selectedIds };
            delete next[seat.id];
            selectedIds = next;
            if (Object.keys(selectedIds).length === 0) {
                selectedCategoryId = null;
            }
        } else {
            if (selectedCategoryId !== null && seatCategoryId !== selectedCategoryId) {
                this.setState({ showCategoryModal: true });
                return;
            }
            if (Object.keys(selectedIds).length >= maxSeats) {
                return;
            }
            selectedIds = {
                ...selectedIds,
                [seat.id]: {
                    id: seat.id,
                    section: seat.section,
                    sectionName: this.getSectionName(seat.section),
                    rowLabel: seat.rowLabel,
                    seatLabel: seat.seatLabel,
                    label: `${seat.rowLabel} / ${seat.seatLabel}`,
                },
            };
            selectedCategoryId = seatCategoryId;
        }

        this.setState({
            selectedIds,
            selectedCategoryId,
        }, () => this.notifySelectionChange(selectedIds, selectedCategoryId));
    }

    handleWheel(e) {
        e.evt.preventDefault();
        this.clearSeatTooltip();
        const stage = this.stageRef.current;
        if (!stage) {
            return;
        }

        const pointer = stage.getPointerPosition();
        if (!pointer) {
            return;
        }

        const scaleBy = 1.08;
        const newUserScale = e.evt.deltaY > 0
            ? this._view.userScale / scaleBy
            : this._view.userScale * scaleBy;

        this.applyZoomAtPoint(pointer, newUserScale, { syncState: true });
    }

    dragBoundFunc(pos) {
        return this.clampStagePosition(pos.x, pos.y, this._view.userScale);
    }

    handleDragEnd(e) {
        if (this._isPinching) {
            return;
        }

        const clamped = this.clampStagePosition(e.target.x(), e.target.y(), this._view.userScale);
        this._view.stageX = clamped.x;
        this._view.stageY = clamped.y;
        e.target.position(clamped);
        this.syncViewToState();
    }

    handleStageDragStart() {
        if (this._isPinching) {
            this.setStageDraggable(false);
            return;
        }
        this.clearSeatTooltip();
    }

    handleSeatMouseEnter(seat) {
        if (this.deviceProfile.isMobile) {
            return;
        }

        const tooltipPosition = this.updateTooltipPosition();
        if (!tooltipPosition) {
            return;
        }

        const selectable = this.isSeatSelectable(seat);
        if (this.containerRef.current) {
            this.containerRef.current.style.cursor = selectable ? 'pointer' : 'not-allowed';
        }

        this.setState({
            hoveredSeat: seat,
            tooltipPosition,
        });
    }

    handleSeatMouseLeave() {
        this.clearSeatTooltip();
    }

    renderStaticSeat(seat, seatScale) {
        const fill = this.getSeatColor(seat);
        const radius = (seat.r || DEFAULT_SEAT_RADIUS) * seatScale;

        return (
            <Group key={`static-${seat.id}`} x={seat.x} y={seat.y} listening={false}>
                <Circle
                    radius={radius}
                    fill={fill}
                    stroke="#ffffff"
                    strokeWidth={1.5}
                    opacity={0.35}
                    listening={false}
                    perfectDrawEnabled={false}
                />
            </Group>
        );
    }

    renderInteractiveSeat(seat, seatScale, showLabels, enableHover) {
        const selected = !!this.state.selectedIds[seat.id];
        const fill = this.getSeatColor(seat);
        const selectable = this.isSeatSelectable(seat);
        const radius = (seat.r || DEFAULT_SEAT_RADIUS) * seatScale;

        return (
            <InteractiveSeat
                key={`interactive-${seat.id}`}
                seat={seat}
                selected={selected}
                fill={fill}
                selectable={selectable}
                radius={radius}
                showLabels={showLabels}
                enableHover={enableHover}
                useLayerSelection={this.deviceProfile.isMobile}
                onSeatClick={this.handleSeatClick}
                onSeatMouseEnter={this.handleSeatMouseEnter}
                onSeatMouseLeave={this.handleSeatMouseLeave}
            />
        );
    }

    getZoomHintText() {
        if (this.props.zoomHintMobile && this.deviceProfile.isMobile) {
            return this.props.zoomHintMobile;
        }

        return this.props.zoomHint || 'Scroll to zoom / drag to pan';
    }

    renderCategoryModal() {
        if (!this.state.showCategoryModal) {
            return null;
        }

        const message = this.props.singleCategoryMessage
            || 'Multiple tickets can be selected only for the same category. Please do a separate order for each category.';
        const okLabel = this.props.okLabel || 'OK';

        return (
            <React.Fragment>
                <div
                    className="modal fade show"
                    style={{ display: 'block' }}
                    tabIndex={-1}
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-body">
                                <p className="mb-0">{message}</p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-primary rounded"
                                    onClick={this.closeCategoryModal}
                                >
                                    {okLabel}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal-backdrop fade show" />
            </React.Fragment>
        );
    }

    render() {
        const canvas = this.props.canvas || { width: 821, height: 1102 };
        const seats = this.props.seats || [];
        const reservation = Object.values(this.state.selectedIds);
        const containerWidth = this.state.containerWidth;
        const viewportHeight = this.state.viewportHeight;
        const totalScale = this.getFitScale() * this.state.userScale;
        const seatScale = this.props.seatScale || this.deviceProfile.seatVisualScale;
        const showLabels = this.props.showSeatLabels === true;
        const enableHover = !this.deviceProfile.isMobile;
        const resetLabel = this.props.resetViewLabel || 'Reset view';

        const staticSeats = [];
        const interactiveSeats = [];

        seats.forEach((seat) => {
            const selectable = this.isSeatSelectable(seat);
            const selected = !!this.state.selectedIds[seat.id];
            if (selectable || selected) {
                interactiveSeats.push(seat);
            } else {
                staticSeats.push(seat);
            }
        });

        const showLegend = this.props.showLegend !== false;
        const zoomInLabel = this.props.zoomInLabel || 'Zoom in';
        const zoomOutLabel = this.props.zoomOutLabel || 'Zoom out';

        return (
            <React.Fragment>
                {showLegend && (
                    <div className="interactive-seating-legend col-12 flex-column flex-md-row d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                        <h3 className="mb-0 me-3">{this.props.legendTitle || 'Legend:'}</h3>
                        {this.renderCategoryLegendItems()}
                        <div className="d-flex flex-column align-items-center mx-2">
                            <span className="d-inline-block rounded-circle" style={{ width: 18, height: 18, backgroundColor: '#dc3545' }} />
                            <small><em>{this.props.bookedLabel || 'Booked'}</em></small>
                        </div>
                        <div className="d-flex flex-column align-items-center mx-2">
                            <span className="d-inline-block rounded-circle bg-warning" style={{ width: 18, height: 18 }} />
                            <small><em>{this.props.selectedLabel || 'Selected'}</em></small>
                        </div>
                        <small className="text-muted">{this.getZoomHintText()}</small>
                    </div>
                )}

                <div
                    ref={this.containerRef}
                    className="interactive-seating-konva-wrapper border rounded-xl overflow-hidden bg-white"
                    style={{ width: '100%', height: viewportHeight, touchAction: 'none', position: 'relative' }}
                >
                    <div className="interactive-seating-map-controls">
                        <button
                            type="button"
                            className="btn btn-sm btn-light border interactive-seating-map-control"
                            onClick={this.resetView}
                            title={resetLabel}
                            aria-label={resetLabel}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="interactive-seating-reset-view-icon"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                                />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-light border interactive-seating-map-control"
                            onClick={this.zoomIn}
                            title={zoomInLabel}
                            aria-label={zoomInLabel}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="interactive-seating-reset-view-icon" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-light border interactive-seating-map-control"
                            onClick={this.zoomOut}
                            title={zoomOutLabel}
                            aria-label={zoomOutLabel}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="interactive-seating-reset-view-icon" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                            </svg>
                        </button>
                    </div>
                    {!this.deviceProfile.isMobile && this.state.hoveredSeat && (
                        <div
                            className="interactive-seat-tooltip"
                            style={{
                                left: this.state.tooltipPosition.x,
                                top: this.state.tooltipPosition.y,
                            }}
                            role="tooltip"
                        >
                            {this.getSeatTooltipText(this.state.hoveredSeat)}
                        </div>
                    )}
                    <Stage
                        ref={this.stageRef}
                        width={containerWidth}
                        height={viewportHeight}
                        scaleX={totalScale}
                        scaleY={totalScale}
                        x={this.state.stageX}
                        y={this.state.stageY}
                        draggable={!this.state.isPinching}
                        dragDistance={this.deviceProfile.isMobile ? MOBILE_DRAG_DISTANCE : 3}
                        dragBoundFunc={this.dragBoundFunc}
                        onDragStart={this.handleStageDragStart}
                        onDragEnd={this.handleDragEnd}
                        onWheel={this.handleWheel}
                        onTap={this.deviceProfile.isMobile ? this.handleStageTap : undefined}
                        onClick={this.deviceProfile.isMobile ? this.handleStageTap : undefined}
                    >
                        <Layer ref={this.staticLayerRef} listening={false} hitGraphEnabled={false}>
                            <Rect
                                x={0}
                                y={0}
                                width={canvas.width}
                                height={canvas.height}
                                fill="#ffffff"
                                listening={false}
                                perfectDrawEnabled={false}
                            />
                            {this.state.bgImage && (
                                <KonvaImage
                                    image={this.state.bgImage}
                                    width={canvas.width}
                                    height={canvas.height}
                                    listening={false}
                                    perfectDrawEnabled={false}
                                />
                            )}
                            {staticSeats.map((seat) => this.renderStaticSeat(seat, seatScale))}
                        </Layer>
                        <Layer listening={true}>
                            {interactiveSeats.map((seat) => this.renderInteractiveSeat(seat, seatScale, showLabels, enableHover))}
                        </Layer>
                    </Stage>
                </div>

                <input
                    type="hidden"
                    name="order[seats][venueMap]"
                    id="interactive-venue-map"
                    value={reservation.length ? JSON.stringify(this.buildVenueMap()) : ''}
                    readOnly
                />

                {this.renderCategoryModal()}
            </React.Fragment>
        );
    }
}

export default InteractiveSeating;
